import { type RefObject, useLayoutEffect, useRef } from "react";
import { valueNames } from "@/modules/Constants";
import type { ResolvedProps } from "@/components/shopwindow/nextVideoBlockModel";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";

type LatestRef = RefObject<{ props: ResolvedProps }>;

/**
 * For the overlay variant: publishes the card's measured height as
 * `--pix-overlay-h` on the shop-window panel, and clears it on unmount or when
 * the card is gone. componentDidMount/DidUpdate and componentWillUnmount timing.
 */
export function useOverlayHeightVar(
  rootRef: RefObject<HTMLDivElement | null>,
  latest: LatestRef,
) {
  const overlay = useRef({
    panelEl: null as HTMLElement | null, // shop-window panel carrying --pix-overlay-h
    measuredOverlayH: null as number | null, // overlay height at the last reserve decision
    measuredPanelH: null as number | null, // panel height at the last reserve decision
    decidedStep: null as number | null, // step index the reserve decision was made for
  });

  const clearOverlayHeightVar = () => {
    const o = overlay.current;
    if (o.panelEl) {
      o.panelEl.style.removeProperty("--pix-overlay-h");
      o.panelEl.classList.remove("shop-window-panel--overlay-reserve");
      o.panelEl = null;
    }
    o.measuredOverlayH = null;
    o.measuredPanelH = null;
    o.decidedStep = null;
  };

  // Decide whether the active method step, at its natural centred position,
  // would be covered by the overlay card. If it fits, leave it alone. If it is
  // covered, publish the card height and switch the window to the single scroll.
  const applyOverlayReserve = (panel: HTMLElement, overlayH: number) => {
    // Drop the reserve class so the step is at its natural position while measuring.
    panel.classList.remove("shop-window-panel--overlay-reserve");
    const wrapper = panel.querySelector(
      ".method-shop-window .articles .swiper-slide-active .method-details-wrapper",
    );
    // The card is pinned `bottom: 0`, so its settled top is the panel bottom
    // minus its height. Its own rect is skewed while it slides in.
    const overlayTop = panel.getBoundingClientRect().bottom - overlayH;
    // No measurable step: reserve conservatively.
    if (!wrapper) {
      panel.style.setProperty("--pix-overlay-h", `${overlayH}px`);
      panel.classList.add("shop-window-panel--overlay-reserve");
      return;
    }
    if (wrapper.getBoundingClientRect().bottom <= overlayTop) {
      panel.style.removeProperty("--pix-overlay-h");
      return;
    }
    panel.style.setProperty("--pix-overlay-h", `${overlayH}px`);
    panel.classList.add("shop-window-panel--overlay-reserve");
  };

  // Publishes the overlay card's height as `--pix-overlay-h` on the shop-window
  // panel so the landscape method step can reserve that space. Re-decides when
  // the step or the overlay/panel size changes, not on every video tick.
  const syncOverlayHeightVar = () => {
    if (latest.current.props.variant !== "overlay") {
      return;
    }
    const el = rootRef.current;
    if (el && el.closest) {
      const panel = el.closest<HTMLElement>(".shop-window-panel");
      if (panel) {
        const o = overlay.current;
        o.panelEl = panel;
        const applicationData =
          latest.current.props.experienceState.applicationData;
        const cfg = applicationData.getValue(valueNames.SHOP_WINDOW_CONFIG) as
          | ShopWindowConfig
          | null
          | undefined;
        const stepKey = cfg ? cfg.index : null;
        const overlayH = el.offsetHeight;
        const panelH = panel.offsetHeight;
        if (
          stepKey !== o.decidedStep ||
          overlayH !== o.measuredOverlayH ||
          panelH !== o.measuredPanelH
        ) {
          o.decidedStep = stepKey;
          o.measuredOverlayH = overlayH;
          o.measuredPanelH = panelH;
          applyOverlayReserve(panel, overlayH);
        }
        return;
      }
    }
    clearOverlayHeightVar();
  };

  useLayoutEffect(() => clearOverlayHeightVar, []);

  useLayoutEffect(() => {
    syncOverlayHeightVar();
  });
}
